import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import { buildApiUrl } from '../../config/api';
import '../../styles/MeusDados.css';

function MeusDados() {
  const [user, setUser] = useState({
    name: '',
    email: '',
    country: '',
    cpf: '',
    rg: '',
    password: '',
    password_confirmation: '',
  });
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [formStatus, setFormStatus] = useState(''); // 'success', 'error', or ''
  const [fieldErrors, setFieldErrors] = useState({});
  // CPF e RG ficam bloqueados após serem preenchidos pela primeira vez
  const [cpfBloqueado, setCpfBloqueado] = useState(false);
  const [rgBloqueado, setRgBloqueado] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirmation, setShowPasswordConfirmation] = useState(false);

  // Funções de validação
  const validateCPF = (cpf) => {
    const cpfRegex = /^\d{3}\.\d{3}\.\d{3}-\d{2}$/;
    return cpfRegex.test(cpf);
  };

  const validateRG = (rg) => {
    const rgRegex = /^[\d\.-]+$/;
    return rgRegex.test(rg) && rg.length >= 5 && rg.length <= 20;
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateName = (name) => {
    const nameRegex = /^[a-zA-ZÀ-ÿ\s]+$/;
    return nameRegex.test(name) && name.length >= 2 && name.length <= 255;
  };

  const validateCountry = (country) => {
    if (!country) return true; // Campo opcional
    const countryRegex = /^[a-zA-ZÀ-ÿ\s]+$/;
    return countryRegex.test(country) && country.length <= 255;
  };

  // Função para sanitizar CPF (aplicar máscara)
  const sanitizeCPF = (value) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 11) {
      return numbers
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d{1,2})/, '$1-$2');
    }
    return value;
  };

  // Função para sanitizar RG
  const sanitizeRG = (value) => {
    return value.replace(/[^0-9.-]/g, '');
  };

  // Função para validar campos em tempo real
  const handleFieldChange = (field, value) => {
    let sanitizedValue = value;
    let isValid = true;
    let errorMessage = '';

    // Sanitização específica por campo
    switch (field) {
      case 'cpf':
        sanitizedValue = sanitizeCPF(value);
        isValid = validateCPF(sanitizedValue);
        if (!isValid && sanitizedValue.length > 0) {
          errorMessage = 'CPF deve estar no formato 000.000.000-00';
        }
        break;
      case 'rg':
        sanitizedValue = sanitizeRG(value);
        isValid = validateRG(sanitizedValue);
        if (!isValid && sanitizedValue.length > 0) {
          errorMessage = 'RG deve conter apenas números, pontos e hífens';
        }
        break;
      case 'email':
        sanitizedValue = value.toLowerCase().trim();
        isValid = validateEmail(sanitizedValue);
        if (!isValid && sanitizedValue.length > 0) {
          errorMessage = 'Email inválido';
        }
        break;
      case 'name':
        sanitizedValue = value.trim();
        isValid = validateName(sanitizedValue);
        if (!isValid && sanitizedValue.length > 0) {
          errorMessage = 'Nome deve conter apenas letras e espaços';
        }
        break;
      case 'country':
        sanitizedValue = value.trim();
        isValid = validateCountry(sanitizedValue);
        if (!isValid && sanitizedValue.length > 0) {
          errorMessage = 'País deve conter apenas letras e espaços';
        }
        break;
      case 'password':
        isValid = value.length === 0 || (value.length >= 6 && value.length <= 72);
        if (!isValid) {
          errorMessage = value.length < 6
            ? 'A senha deve ter pelo menos 6 caracteres'
            : 'A senha deve ter no máximo 72 caracteres';
        }
        break;
      case 'password_confirmation':
        isValid = value.length === 0 || value === user.password;
        if (!isValid) {
          errorMessage = 'As senhas não coincidem';
        }
        break;
      default:
        break;
    }

    // Atualizar estado
    const updatedUser = { ...user, [field]: sanitizedValue };
    setUser(updatedUser);
    
    // Atualizar erros
    setFieldErrors(prev => ({
      ...prev,
      [field]: errorMessage,
      ...(field === 'password' && updatedUser.password_confirmation
        ? {
            password_confirmation:
              updatedUser.password === updatedUser.password_confirmation
                ? ''
                : 'As senhas não coincidem'
          }
        : {})
    }));
  };

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(buildApiUrl('user'), {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });
        setUser(prev => ({
          ...prev,
          ...response.data,
          password: '',
          password_confirmation: '',
        }));
        if (response.data.cpf) setCpfBloqueado(true);
        if (response.data.rg) setRgBloqueado(true);
      } catch (error) {

        setError('Erro ao buscar dados do usuário!');
        setFormStatus('error');
      }
    };

    fetchUserData();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validação final antes do envio
    const errors = {};
    
    if (!validateName(user.name)) {
      errors.name = 'Nome inválido';
    }
    if (!validateEmail(user.email)) {
      errors.email = 'Email inválido';
    }
    if (!cpfBloqueado && !validateCPF(user.cpf)) {
      errors.cpf = 'CPF inválido';
    }
    if (!rgBloqueado && !validateRG(user.rg)) {
      errors.rg = 'RG inválido';
    }
    if (!validateCountry(user.country)) {
      errors.country = 'País inválido';
    }
    if (user.password && (user.password.length < 6 || user.password.length > 72)) {
      errors.password = user.password.length < 6
        ? 'A senha deve ter pelo menos 6 caracteres'
        : 'A senha deve ter no máximo 72 caracteres';
    }
    if (user.password && user.password !== user.password_confirmation) {
      errors.password_confirmation = 'As senhas não coincidem';
    }
    
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      setError('Por favor, corrija os erros nos campos destacados');
      setFormStatus('error');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(buildApiUrl('user'), user, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      setMessage('Dados atualizados com sucesso!');
      setError('');
      setFieldErrors({});
      setFormStatus('success');
      setUser(prev => ({
        ...prev,
        password: '',
        password_confirmation: '',
      }));

      // Clear the success message and reset form status after 3 seconds
      setTimeout(() => {
        setMessage('');
        setFormStatus('');
      }, 3000);
    } catch (error) {

      
      // Tratar erros de validação do backend
      if (error.response && error.response.data && error.response.data.errors) {
        setFieldErrors(error.response.data.errors);
        setError('Verifique os campos destacados');
      } else {
        setError('Erro ao atualizar dados do usuário!');
      }
      
      setMessage('');
      setFormStatus('error');

      // Clear the error message and reset form status after 5 seconds
      setTimeout(() => {
        setError('');
        setFormStatus('');
      }, 5000);
    }
  };

  return (
    <div className={`meus-dados-page ${formStatus}`}>
      {/* Hero Section */}
      <div className="meus-dados-hero">
        <div className="meus-dados-container">
          <h1>Meus Dados</h1>
          <p className="hero-subtitle">Mantenha suas informações sempre atualizadas</p>
        </div>
      </div>

      {/* Conteúdo Principal */}
      <div className="meus-dados-container">
        <div className="meus-dados-content">
          <div className="info-section">
            <h2>Informações Pessoais</h2>
            <p>Todos os dados são de extrema importância, pois serão usados para gerar os certificados dos cursos que você concluir.</p>
            <p><strong>Importante:</strong> Não altere seu Nome para não haver erros na hora de autenticar seus certificados já emitidos. Cada aluno deve ter seu cadastro único.</p>
          </div>

          {message && <div className="success-message">{message}</div>}
          {error && <div className="error-message">{error}</div>}
          
          <form className="meus-dados-form" onSubmit={handleSubmit}>
            <div className="form-grid">
              <div className="input-group">
                <div className="input-container">
                  <input
                    type="text"
                    id="country"
                    value={user.country}
                    onChange={(e) => handleFieldChange('country', e.target.value)}
                    placeholder=" "
                    maxLength="255"
                    className={fieldErrors.country ? 'error' : ''}
                  />
                  <label htmlFor="country">País</label>
                  {fieldErrors.country && (
                    <span className="error-text">{fieldErrors.country}</span>
                  )}
                </div>
              </div>

              <div className="input-group">
                <div className="input-container">
                  <input
                    type="text"
                    id="name"
                    value={user.name}
                    onChange={(e) => handleFieldChange('name', e.target.value)}
                    required
                    placeholder=" "
                    maxLength="255"
                    className={fieldErrors.name ? 'error' : ''}
                  />
                  <label htmlFor="name">Nome Completo</label>
                  {fieldErrors.name && (
                    <span className="error-text">{fieldErrors.name}</span>
                  )}
                </div>
              </div>

              <div className="input-group">
                <div className="input-container">
                  <input
                    type="email"
                    id="email"
                    value={user.email}
                    onChange={(e) => handleFieldChange('email', e.target.value)}
                    required
                    placeholder=" "
                    maxLength="255"
                    className={fieldErrors.email ? 'error' : ''}
                    autoComplete="email"
                  />
                  <label htmlFor="email">E-mail</label>
                  {fieldErrors.email && (
                    <span className="error-text">{fieldErrors.email}</span>
                  )}
                </div>
              </div>

              <div className="input-group">
                <div className="input-container">
                  <input
                    type="text"
                    id="rg"
                    value={user.rg}
                    onChange={(e) => !rgBloqueado && handleFieldChange('rg', e.target.value)}
                    readOnly={rgBloqueado}
                    required
                    placeholder=" "
                    maxLength="20"
                    className={`${fieldErrors.rg ? 'error' : ''} ${rgBloqueado ? 'field-locked' : ''}`}
                    title={rgBloqueado ? 'RG já registrado e não pode ser alterado' : ''}
                  />
                  <label htmlFor="rg">RG {rgBloqueado && <span style={{fontSize:'0.75rem',color:'#7f8c8d',marginLeft:'4px'}}>🔒 bloqueado</span>}</label>
                  {!rgBloqueado && fieldErrors.rg && (
                    <span className="error-text">{fieldErrors.rg}</span>
                  )}
                  {rgBloqueado && (
                    <span className="info-text" style={{fontSize:'0.75rem',color:'#7f8c8d',display:'block',marginTop:'4px'}}>RG registrado. Para alterações, entre em contato com o suporte.</span>
                  )}
                </div>
              </div>

              <div className="input-group">
                <div className="input-container">
                  <input
                    type="text"
                    id="cpf"
                    value={user.cpf}
                    onChange={(e) => !cpfBloqueado && handleFieldChange('cpf', e.target.value)}
                    readOnly={cpfBloqueado}
                    required
                    placeholder=" "
                    maxLength="14"
                    className={`${fieldErrors.cpf ? 'error' : ''} ${cpfBloqueado ? 'field-locked' : ''}`}
                    title={cpfBloqueado ? 'CPF já registrado e não pode ser alterado' : ''}
                  />
                  <label htmlFor="cpf">CPF {cpfBloqueado && <span style={{fontSize:'0.75rem',color:'#7f8c8d',marginLeft:'4px'}}>🔒 bloqueado</span>}</label>
                  {!cpfBloqueado && fieldErrors.cpf && (
                    <span className="error-text">{fieldErrors.cpf}</span>
                  )}
                  {cpfBloqueado && (
                    <span className="info-text" style={{fontSize:'0.75rem',color:'#7f8c8d',display:'block',marginTop:'4px'}}>CPF registrado. Para alterações, entre em contato com o suporte.</span>
                  )}
                </div>
              </div>

              <div className="input-group">
                <div className="input-container profile-password-field">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    value={user.password}
                    onChange={(e) => handleFieldChange('password', e.target.value)}
                    placeholder=" "
                    minLength="6"
                    maxLength="72"
                    autoComplete="new-password"
                    className={fieldErrors.password ? 'error' : ''}
                  />
                  <label htmlFor="password">Nova Senha</label>
                  <button
                    type="button"
                    className="profile-password-toggle"
                    onClick={() => setShowPassword(prev => !prev)}
                    aria-label={showPassword ? 'Ocultar nova senha' : 'Mostrar nova senha'}
                    aria-pressed={showPassword}
                  >
                    <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                  </button>
                  {fieldErrors.password && (
                    <span className="error-text">{fieldErrors.password}</span>
                  )}
                  <small>Deixe em branco para manter a senha atual. Mínimo de 6 caracteres.</small>
                </div>
              </div>

              <div className="input-group">
                <div className="input-container profile-password-field">
                  <input
                    type={showPasswordConfirmation ? 'text' : 'password'}
                    id="password_confirmation"
                    value={user.password_confirmation}
                    onChange={(e) => handleFieldChange('password_confirmation', e.target.value)}
                    placeholder=" "
                    minLength="6"
                    maxLength="72"
                    autoComplete="new-password"
                    className={fieldErrors.password_confirmation ? 'error' : ''}
                  />
                  <label htmlFor="password_confirmation">Confirmar Nova Senha</label>
                  <button
                    type="button"
                    className="profile-password-toggle"
                    onClick={() => setShowPasswordConfirmation(prev => !prev)}
                    aria-label={showPasswordConfirmation ? 'Ocultar confirmação da senha' : 'Mostrar confirmação da senha'}
                    aria-pressed={showPasswordConfirmation}
                  >
                    <FontAwesomeIcon icon={showPasswordConfirmation ? faEyeSlash : faEye} />
                  </button>
                  {fieldErrors.password_confirmation && (
                    <span className="error-text">{fieldErrors.password_confirmation}</span>
                  )}
                </div>
              </div>
            </div>

            <button type="submit" className="submit-btn">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
              </svg>
              Salvar Alterações
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

export default MeusDados;
